import { FC, useMemo, useEffect, useState, useRef, useLayoutEffect } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { isNotEmpty } from '../../../../../_dashboard/helpers'
import { Facility, FacilityTranslation } from '../core/_models'
import clsx from 'clsx'
import { useListView } from '../core/ListViewProvider'
import { DataLoading } from '../../../../../_dashboard/partials/components/loading/DataLoading'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { createOne, getAll, patchOne } from '../../../../services/common_requests'
import { useIntl } from 'react-intl'
import { toAbsoluteUrl } from '../../../../../_dashboard/helpers'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useQueryRequest } from '../core/QueryRequestProvider'
import { getBackofficeDefaultLanguage } from '../../../tours-management/configurations/models/personType'

type Props = {
  isFacilityLoading: boolean
  Facility: Facility
}

function extractLanguagesList(body: unknown): unknown[] {
  if (!body) return []
  if (Array.isArray(body)) return body
  if (typeof body === 'object' && body !== null) {
    const o = body as { data?: unknown }
    if (Array.isArray(o.data)) return o.data
    const nested = (o.data as { data?: unknown[] } | undefined)?.data
    if (Array.isArray(nested)) return nested
  }
  return []
}

const editFacilitySchema = Yup.object().shape({
  translations: Yup.array().of(
    Yup.object().shape({
      language: Yup.string(),
      title: Yup.string().max(120),
      _id: Yup.string(),
    }),
  ),
})

function normLang(l: unknown): string {
  return String(l ?? '')
    .trim()
    .toLowerCase()
}

function mergeFacilityForForm(facility: Facility, langCodes?: string[]): Facility {
  const raw = facility.translations?.length ? [...facility.translations] : []
  const fromApi = langCodes?.map(normLang).filter(Boolean) ?? []
  const existingKeys = Array.from(
    new Set(raw.map((t) => normLang(t.language)).filter(Boolean)),
  )
  const codes = Array.from(new Set(fromApi.length ? fromApi : existingKeys))
  const unified = codes.length ? codes : ['en']
  const byLang = new Map(raw.map((t) => [normLang(t.language), t]))
  return {
    ...facility,
    translations: unified.map((code) => {
      const prev = byLang.get(code)
      return {
        ...prev,
        language: code,
        title: prev?.title ?? '',
        _id: prev?._id,
      }
    }),
  }
}

function mergeTranslationsPreserveInput(
  facility: Facility,
  langCodes: string[],
  previous: FacilityTranslation[],
): FacilityTranslation[] {
  const fresh = mergeFacilityForForm(facility, langCodes).translations
  const map = new Map(previous.map((t) => [normLang(t.language), t]))
  return fresh.map((t) => {
    const old = map.get(normLang(t.language))
    if (old === undefined) return t
    return {
      ...t,
      title: old.title ?? t.title ?? '',
      _id: old._id ?? t._id,
    }
  })
}

const FacilityEditModalForm: FC<Props> = ({ Facility, isFacilityLoading }) => {
  const intl = useIntl()
  const LANGUAGE_NAMES: { [key: string]: string } = useMemo(
    () => ({
      en: intl.formatMessage({ id: 'COMMON.ENGLISH_LANGUAGE' }),
      fr: intl.formatMessage({ id: 'COMMON.FRENSH_LANGUAGE' }),
      es: intl.formatMessage({ id: 'COMMON.ESPANGOL_LANGUAGE' }),
      de: intl.formatMessage({ id: 'COMMON.GERMAN_LANGUAGE' }),
      ar: intl.formatMessage({ id: 'COMMON.ARABIC_LANGUAGE' }),
    }),
    [intl],
  )

  const { itemIdForUpdate, setItemIdForUpdate } = useListView()
  const { refetchData } = useQueryResponse()
  const { state } = useQueryRequest()
  const [query] = useState<any>(state)
  const [languages, setLanguages] = useState<{ code: string; name: string }[]>([])
  const [selectedLanguage, setSelectedLanguage] = useState(
    () => normLang(getBackofficeDefaultLanguage()) || 'fr',
  )
  const [iconPreview, setIconPreview] = useState<string | null>(null)
  const [uploadedIcon, setUploadedIcon] = useState<File | null>(null)

  const langCodes = useMemo(() => languages.map((l) => normLang(l.code)).filter(Boolean), [languages])

  const facilityInitial = useMemo(
    () => mergeFacilityForForm(Facility, langCodes),
    [Facility, langCodes],
  )

  const serverFacilitySig = useMemo(() => {
    const tr = Facility?.translations || []
    const idSig = tr
      .map((t) => `${String(t._id ?? 'x')}:${normLang(t.language)}`)
      .sort()
      .join(';')
    return `${String(Facility?._id ?? 'new')}:${isFacilityLoading ? 1 : 0}:${idSig}`
  }, [Facility, isFacilityLoading])

  useEffect(() => {
    void getAll(query, '/languages').then((data) => {
      const codes = extractLanguagesList(data).map(String).filter(Boolean)
      if (!codes.length) return
      const formattedLanguages = codes.map((code: string) => ({
        code: normLang(code) || code,
        name: LANGUAGE_NAMES[normLang(code) || code] || normLang(code).toUpperCase() || code.toUpperCase(),
      }))
      setLanguages(formattedLanguages)
    })
  }, [query, LANGUAGE_NAMES])

  useEffect(() => {
    if (!languages.length) return
    setSelectedLanguage((cur) => {
      const codes = languages.map((l) => normLang(l.code))
      const normalized = normLang(cur)
      if (codes.includes(normalized)) return cur
      const def = normLang(getBackofficeDefaultLanguage())
      return codes.includes(def) ? def : codes[0]
    })
  }, [languages])

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetchData()
    }
    setItemIdForUpdate(undefined)
  }

  const handleIconChange = (file: File | null) => {
    if (file) {
      setUploadedIcon(file)
      setIconPreview(URL.createObjectURL(file))
    }
  }

  const translationsSnapshotRef = useRef<FacilityTranslation[]>([])
  const lastAppliedLangJoinRef = useRef<string>('')

  const formik = useFormik({
    initialValues: facilityInitial,
    validationSchema: editFacilitySchema,
    enableReinitialize: false,
    onSubmit: async (values, { setSubmitting }) => {
      const selectedTranslation = values.translations.find(
        (t) => normLang(t.language) === normLang(selectedLanguage),
      )
      const trimmed = selectedTranslation?.title?.trim() ?? ''

      setSubmitting(true)
      try {
        if (!trimmed || trimmed.length < 3) {
          toast.error(
            intl.formatMessage({
              id: 'ADMIN.COMMON.VALIDATION.FACILITY_TITLE_MIN',
              defaultMessage: 'Facility title must be at least 3 characters.',
            }),
          )
          return
        }

        if (isNotEmpty(itemIdForUpdate)) {
          let response
          if (selectedTranslation?._id) {
            const formData = new FormData()
            if (uploadedIcon) {
              formData.append('icon', uploadedIcon)
            }
            formData.append('title', trimmed)

            response = await patchOne(
              formData,
              `/facilities/translation/${selectedTranslation._id}`,
            )
          } else {
            const payload = {
              language: normLang(selectedLanguage) || 'en',
              title: trimmed,
            }
            response = await createOne(payload, `/facilities/${itemIdForUpdate}/translations`)
          }
          if (response?.payload?.code === 2021) {
            toast.success(
              intl.formatMessage({ id: 'ADMIN.COMMON.SUCCESS.FACILITY_UPDATED_SUCCESSFULLY' }),
              { position: 'top-right', autoClose: 4000 },
            )
            cancel(true)
          } else if (response?.payload?.code === 4401) {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ID_NOT_EXIST' }), {
              position: 'top-right',
              autoClose: 4000,
            })
          } else if (response?.payload?.code === 9100) {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ID_NOT_FOUND' }), {
              position: 'top-right',
              autoClose: 4000,
            })
          } else {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_FACILITY' }), {
              position: 'top-right',
              autoClose: 4000,
            })
          }
        } else {
          if (!uploadedIcon) {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.FACILITY_ICON_REQUIRED',
                defaultMessage: 'Please upload an icon for this facility.',
              }),
            )
            return
          }
          const lang = normLang(selectedLanguage) || 'en'
          const formData = new FormData()
          formData.append('image', uploadedIcon)
          formData.append('title', trimmed)
          formData.append('category', 'general')
          formData.append('translations[0][language]', lang)
          formData.append('translations[0][title]', trimmed)

          const response = await createOne(formData, '/facilities')
          if (response?.payload?.code === 2021) {
            toast.success(
              intl.formatMessage({ id: 'ADMIN.COMMON.SUCCESS.FACILITY_ADDED_SUCCESSFULLY' }),
              { position: 'top-right', autoClose: 4000 },
            )
            cancel(true)
          } else if (response?.payload?.code === 4400) {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.NAME_ALREADY_EXIST' }), {
              position: 'top-right',
              autoClose: 4000,
            })
          } else {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_FACILITY' }), {
              position: 'top-right',
              autoClose: 4000,
            })
          }
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  useLayoutEffect(() => {
    translationsSnapshotRef.current = formik.values.translations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.translations])

  useEffect(() => {
    if (isFacilityLoading) return
    void formik.resetForm({ values: mergeFacilityForForm(Facility, langCodes) })
    lastAppliedLangJoinRef.current = langCodes.join(',')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverFacilitySig])

  useEffect(() => {
    if (isFacilityLoading) return
    const join = langCodes.join(',')
    if (!join) return
    if (lastAppliedLangJoinRef.current === join) return
    lastAppliedLangJoinRef.current = join
    const prev = translationsSnapshotRef.current
    const next = mergeTranslationsPreserveInput(Facility, langCodes, prev)
    void formik.setFieldValue('translations', next, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langCodes.join(','), isFacilityLoading])

  const selectedTranslation = formik.values.translations.find(
    (t) => normLang(t.language) === normLang(selectedLanguage),
  )

  const translationIndex =
    selectedTranslation !== undefined
      ? formik.values.translations.findIndex(
          (t) => normLang(t.language) === normLang(selectedLanguage),
        )
      : -1

  const setTitleForLanguage = (title: string) => {
    const langs = [...formik.values.translations]
    let idx = langs.findIndex((t) => normLang(t.language) === normLang(selectedLanguage))
    if (idx === -1) {
      langs.push({ language: selectedLanguage, title })
      void formik.setFieldValue('translations', langs)
      return
    }
    langs[idx] = { ...langs[idx], title }
    void formik.setFieldValue('translations', langs)
    void formik.setFieldTouched(`translations[${idx}].title`, true)
  }

  return (
    <>
      <form
        id='kt_modal_add_facility_form'
        className='form'
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <div className='d-flex flex-column gap-5'>
          <div className='fv-row mb-4'>
            <label className='d-block fw-bold fs-6 mb-5'>
              {intl.formatMessage({ id: 'COLUMNS.ICON' })}
            </label>

            {!itemIdForUpdate ? (
              <div
                className='image-input image-input-outline'
                data-kt-image-input='true'
                style={{
                  backgroundImage: iconPreview
                    ? `url('${iconPreview}')`
                    : `url('${toAbsoluteUrl('/media/svg/files/blank-image.svg')}')`,
                }}
              >
                <div className='image-input-wrapper w-125px h-125px'></div>
                <label
                  className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                  data-kt-image-input-action='change'
                  title='Change icon'
                >
                  <i className='bi bi-pencil-fill fs-7'></i>
                  <input
                    type='file'
                    name='icon'
                    accept='.png, .jpg, .jpeg'
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleIconChange(e.target.files[0])
                      }
                    }}
                  />
                </label>
              </div>
            ) : (
              <div
                className='image-input image-input-outline'
                data-kt-image-input='true'
                style={{
                  backgroundImage: `url('${
                    iconPreview ||
                    (process.env.REACT_APP_API_URL_IMAGE || '') + (Facility?.icon?.url || '')
                  }')`,
                }}
              >
                <div className='image-input-wrapper w-125px h-125px'></div>
                <label
                  className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                  data-kt-image-input-action='change'
                  data-bs-toggle='tooltip'
                  title='Change avatar'
                >
                  <i className='bi bi-pencil-fill fs-7'></i>
                  <input
                    type='file'
                    name='icon'
                    accept='.png, .jpg, .jpeg'
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        handleIconChange(e.target.files[0])
                      }
                    }}
                  />
                  <input type='hidden' name='avatar_remove' />
                </label>
              </div>
            )}

            <div className='form-text'>{intl.formatMessage({ id: 'COMMUN.ALLOWED_FILE' })}</div>
          </div>

          <div className='fv-row mb-4'>
            <label className='required fw-bold fs-6 mb-2' htmlFor='kt_modal_facility_language'>
              {intl.formatMessage({ id: 'COLUMNS.LANGUAGE' })}
            </label>
            <select
              id='kt_modal_facility_language'
              name='facilityLanguage'
              onChange={(e) => setSelectedLanguage(e.target.value)}
              value={selectedLanguage}
              className='form-select form-select-solid'
              disabled={formik.isSubmitting || isFacilityLoading}
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          <div className='fv-row mb-4'>
            <label className='required fw-bold fs-6 mb-2' htmlFor='kt_modal_facility_title'>
              {intl.formatMessage({ id: 'COLUMNS.NAME' })}
            </label>
            <input
              id='kt_modal_facility_title'
              placeholder={intl.formatMessage({ id: 'COLUMNS.NAME' })}
              value={selectedTranslation?.title ?? ''}
              onChange={(e) => setTitleForLanguage(e.target.value)}
              type='text'
              name='facilityTitle'
              className={clsx(
                'form-control form-control-solid',
                translationIndex >= 0 &&
                  formik.touched.translations?.[translationIndex]?.title &&
                  (formik.errors.translations?.[translationIndex] as { title?: string } | undefined)
                    ?.title
                  ? 'is-invalid'
                  : '',
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || isFacilityLoading}
            />
          </div>
        </div>

        <div className='d-flex justify-content-end gap-3 pt-4'>
          <button
            type='button'
            onClick={() => cancel()}
            className='btn btn-light-primary fw-semibold px-6'
            data-kt-facilities-modal-action='cancel'
            disabled={formik.isSubmitting || isFacilityLoading}
          >
            {intl.formatMessage({ id: 'HEADER.RESET' })}
          </button>

          <button
            type='submit'
            className='btn btn-primary fw-semibold px-8'
            data-kt-facilities-modal-action='submit'
            disabled={isFacilityLoading || formik.isSubmitting}
          >
            <span className='indicator-label'>{intl.formatMessage({ id: 'HEADER.SAVE' })}</span>
            {(formik.isSubmitting || isFacilityLoading) && (
              <span className='indicator-progress'>
                {intl.formatMessage({ id: 'COMMON.PLEASE_WAIT' })}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
      {isFacilityLoading && <DataLoading />}
    </>
  )
}

export { FacilityEditModalForm }
